import React, { useEffect, useState, createElement, Component } from 'react';
import { SearchIcon, FilterIcon, ChevronDownIcon, DownloadIcon, CheckIcon, XIcon, RefreshCwIcon, CheckCircleIcon, CalendarIcon, DollarSignIcon, QrCodeIcon, FileTextIcon, ArrowRightIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock merchant data
const merchantsData = [{
  id: 1,
  merchantId: 'MERCH001',
  name: 'ABC Corporation',
  businessType: 'Retail',
  status: 'active',
  region: 'North',
  storeLocation: 'Main Street, Cityville',
  paymentMethods: ['visa', 'mastercard', 'qr'],
  onboardingDate: '2023-05-15',
  lastQRGenerated: '2023-10-25'
}, {
  id: 2,
  merchantId: 'MERCH002',
  name: 'XYZ Enterprises',
  businessType: 'E-commerce',
  status: 'active',
  region: 'South',
  storeLocation: 'Online',
  paymentMethods: ['visa', 'mastercard', 'qr', 'rtp'],
  onboardingDate: '2023-06-22',
  lastQRGenerated: '2023-10-28'
}, {
  id: 3,
  merchantId: 'MERCH003',
  name: 'Local Grocers',
  businessType: 'Grocery',
  status: 'inactive',
  region: 'East',
  storeLocation: 'Oak Avenue, Townsville',
  paymentMethods: ['visa', 'qr'],
  onboardingDate: '2023-04-10',
  lastQRGenerated: '2023-09-15'
}, {
  id: 4,
  merchantId: 'MERCH004',
  name: 'Tech Solutions Inc',
  businessType: 'Technology',
  status: 'pending',
  region: 'West',
  storeLocation: 'Tech Park, Innovation City',
  paymentMethods: ['visa', 'mastercard', 'qr', 'rtp'],
  onboardingDate: '2023-07-05',
  lastQRGenerated: null
}, {
  id: 5,
  merchantId: 'MERCH005',
  name: 'Metro Retailers',
  businessType: 'Retail',
  status: 'active',
  region: 'Central',
  storeLocation: 'Shopping Mall, Downtown',
  paymentMethods: ['visa', 'mastercard', 'qr'],
  onboardingDate: '2023-03-18',
  lastQRGenerated: '2023-10-10'
}, {
  id: 6,
  merchantId: 'MERCH006',
  name: 'Coffee House Chain',
  businessType: 'Food & Beverage',
  status: 'active',
  region: 'North',
  storeLocation: 'Multiple Locations',
  paymentMethods: ['visa', 'mastercard', 'qr'],
  onboardingDate: '2023-02-14',
  lastQRGenerated: '2023-10-20'
}, {
  id: 7,
  merchantId: 'MERCH007',
  name: 'Fashion Boutique',
  businessType: 'Retail',
  status: 'active',
  region: 'West',
  storeLocation: 'Fashion Street, Style District',
  paymentMethods: ['visa', 'mastercard', 'qr'],
  onboardingDate: '2023-01-30',
  lastQRGenerated: '2023-10-15'
}];
interface Merchant {
  id: number;
  merchantId: string;
  name: string;
  businessType: string;
  status: string;
  region: string;
  storeLocation: string;
  paymentMethods: string[];
  onboardingDate: string;
  lastQRGenerated: string | null;
}
interface QRParameters {
  amount: string;
  currency: string;
  expiryDate: string;
  paymentMethod: string;
  includeAmount: boolean;
}
interface GeneratedQR {
  merchantId: string;
  merchantName: string;
  status: 'pending' | 'generated' | 'failed';
  qrUrl?: string;
  errorMessage?: string;
}
const BulkQR: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>(merchantsData);
  const [selectedMerchants, setSelectedMerchants] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  // QR parameters
  const [qrParams, setQrParams] = useState<QRParameters>({
    amount: '',
    currency: 'PKR',
    expiryDate: '',
    paymentMethod: 'qr',
    includeAmount: false
  });
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedQRs, setGeneratedQRs] = useState<GeneratedQR[]>([]);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // Mock history data
  const [bulkHistory, setBulkHistory] = useState([{
    id: 1,
    date: '2023-10-28T14:30:00Z',
    user: 'admin@example.com',
    merchantCount: 5,
    status: 'completed',
    downloadAvailable: true
  }, {
    id: 2,
    date: '2023-10-25T11:15:00Z',
    user: 'manager@example.com',
    merchantCount: 3,
    status: 'completed',
    downloadAvailable: true
  }, {
    id: 3,
    date: '2023-10-20T09:45:00Z',
    user: 'admin@example.com',
    merchantCount: 7,
    status: 'completed',
    downloadAvailable: true
  }]);
  // Filter merchants based on search and filters
  useEffect(() => {
    let filtered = merchants;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(merchant => merchant.name.toLowerCase().includes(term) || merchant.merchantId.toLowerCase().includes(term) || merchant.storeLocation.toLowerCase().includes(term));
    }
    if (regionFilter) {
      filtered = filtered.filter(merchant => merchant.region === regionFilter);
    }
    if (statusFilter) {
      filtered = filtered.filter(merchant => merchant.status === statusFilter);
    }
    if (businessTypeFilter) {
      filtered = filtered.filter(merchant => merchant.businessType === businessTypeFilter);
    }
    setFilteredMerchants(filtered);
  }, [merchants, searchTerm, regionFilter, statusFilter, businessTypeFilter]);
  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedMerchants([]);
    } else {
      setSelectedMerchants(filteredMerchants.map(merchant => merchant.id));
    }
    setSelectAll(!selectAll);
  };
  // Handle individual merchant selection
  const handleSelectMerchant = (merchantId: number) => {
    if (selectedMerchants.includes(merchantId)) {
      setSelectedMerchants(selectedMerchants.filter(id => id !== merchantId));
      setSelectAll(false);
    } else {
      setSelectedMerchants([...selectedMerchants, merchantId]);
      if (selectedMerchants.length + 1 === filteredMerchants.length) {
        setSelectAll(true);
      }
    }
  };
  // Handle QR parameters change
  const handleQRParamChange = (param: keyof QRParameters, value: string | boolean) => {
    setQrParams({
      ...qrParams,
      [param]: value
    });
  };
  // Generate QR codes
  const handleGenerateQRs = () => {
    if (selectedMerchants.length === 0) {
      alert('Please select at least one merchant');
      return;
    }
    setIsGenerating(true);
    setGenerationProgress(0);
    setShowGenerationModal(true);
    // Initialize generated QRs
    const initialQRs = selectedMerchants.map(id => {
      const merchant = merchants.find(m => m.id === id)!;
      return {
        merchantId: merchant.merchantId,
        merchantName: merchant.name,
        status: 'pending' as const
      };
    });
    setGeneratedQRs(initialQRs);
    // Simulate QR generation with progress
    let progress = 0;
    const totalMerchants = selectedMerchants.length;
    const interval = setInterval(() => {
      progress += 1;
      setGenerationProgress(Math.floor(progress / totalMerchants * 100));
      // Update status of current merchant
      if (progress <= totalMerchants) {
        setGeneratedQRs(prev => {
          const updated = [...prev];
          const randomSuccess = Math.random() > 0.1; // 90% success rate
          updated[progress - 1] = {
            ...updated[progress - 1],
            status: randomSuccess ? 'generated' : 'failed',
            qrUrl: randomSuccess ? `https://example.com/qr/${updated[progress - 1].merchantId}` : undefined,
            errorMessage: randomSuccess ? undefined : 'Failed to generate QR code'
          };
          return updated;
        });
      }
      if (progress >= totalMerchants) {
        clearInterval(interval);
        setIsGenerating(false);
      }
    }, 500);
  };
  // Format date string
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-2117" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-2118" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-2119" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-2120" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-2121" />;
    }
  };
  // Get payment methods as badges
  const getPaymentMethodBadges = (methods: string[]) => {
    return <div className="flex flex-wrap gap-1" data-id="element-2122">
        {methods.map(method => <span key={method} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300" data-id="element-2123">
            {method.toUpperCase()}
          </span>)}
      </div>;
  };
  // Generate random 8-digit TID
  const generateRandomTID = () => {
    return Math.floor(10000000 + Math.random() * 90000000).toString();
  };
  // Download QR codes as PDF
  const handleDownloadQRs = async (format: 'png' | 'csv') => {
    if (format === 'png') {
      const successfulQRs = generatedQRs.filter(qr => qr.status === 'generated');
      if (successfulQRs.length === 0) {
        alert('No successful QR codes to download');
        return;
      }
      // Create PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      // Process each merchant QR code
      for (let index = 0; index < successfulQRs.length; index++) {
        const qr = successfulQRs[index];
        const merchant = merchants.find(m => m.merchantId === qr.merchantId);
        if (merchant) {
          // Add new page for each merchant (except first)
          if (index > 0) {
            pdf.addPage();
          }
          // Generate random 8-digit TID
          const randomTID = generateRandomTID();
          // Generate proper EMVCo QR code
          const qrValue = `00020101021152${merchant.merchantId.length.toString().padStart(2, '0')}${merchant.merchantId}5802PK5913${merchant.name.length.toString().padStart(2, '0')}${merchant.name}6304`;
          try {
            // Create a temporary container for QR code
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            document.body.appendChild(tempContainer);
            // Render QR code using QRCodeSVG
            const qrSize = 300; // Size in pixels for better quality
            const qrBorder = 20; // White border around QR code
            // Create container with white background and border
            const qrContainer = document.createElement('div');
            qrContainer.style.width = `${qrSize + 2 * qrBorder}px`;
            qrContainer.style.height = `${qrSize + 2 * qrBorder}px`;
            qrContainer.style.backgroundColor = '#FFFFFF';
            qrContainer.style.padding = `${qrBorder}px`;
            qrContainer.style.boxSizing = 'border-box';
            qrContainer.style.border = '1px solid #E5E5E5';
            qrContainer.style.borderRadius = '8px';
            tempContainer.appendChild(qrContainer);
            // Render QRCodeSVG to the DOM
            const qrCodeElement = document.createElement('div');
            qrCodeElement.style.width = `${qrSize}px`;
            qrCodeElement.style.height = `${qrSize}px`;
            qrContainer.appendChild(qrCodeElement);
            // Use ReactDOM to render the QRCodeSVG component
            const ReactDOM = require('react-dom');
            ReactDOM.render(<QRCodeSVG value={qrValue} size={qrSize - 2 * qrBorder} level="H" // High error correction for better scanning
            includeMargin={true} data-id="element-2124" />, qrCodeElement);
            // Convert the rendered QR code to canvas
            const html2canvas = require('html2canvas');
            const canvas = await html2canvas(qrContainer, {
              backgroundColor: '#FFFFFF',
              scale: 2 // Higher scale for better quality
            });
            // Get image data from canvas
            const qrImageData = canvas.toDataURL('image/png');
            // Add QR code to PDF (centered)
            const pdfWidth = 210; // A4 width in mm
            const qrPdfSize = 140; // QR code size in mm
            const qrX = (pdfWidth - qrPdfSize) / 2;
            const qrY = 20; // Top margin
            // Add QR code image to PDF
            pdf.addImage(qrImageData, 'PNG', qrX, qrY, qrPdfSize, qrPdfSize);
            // Add merchant name (centered, bold)
            pdf.setFontSize(22);
            pdf.setFont('helvetica', 'bold');
            pdf.text(merchant.name, pdfWidth / 2, qrY + qrPdfSize + 20, {
              align: 'center'
            });
            // Add MID (Merchant ID)
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`MID: ${merchant.merchantId}`, pdfWidth / 2, qrY + qrPdfSize + 30, {
              align: 'center'
            });
            // Add TID (Random 8-digit Till ID)
            pdf.text(`TID: ${randomTID}`, pdfWidth / 2, qrY + qrPdfSize + 40, {
              align: 'center'
            });
            // Add payment scheme logos with proper aspect ratios
            const logoY = qrY + qrPdfSize + 55; // Y position of logos
            const logoHeight = 10; // Fixed height in mm for all logos
            const logoSpacing = 6; // Space between logos in mm
            // Define logo aspect ratios (width:height)
            const logoConfigs = [{
              url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png',
              aspectRatio: 2.5 // Visa is wider
            }, {
              url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png',
              aspectRatio: 1.6 // Mastercard circles
            }, {
              url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/UnionPay_logo.svg/200px-UnionPay_logo.svg.png',
              aspectRatio: 2.0 // UnionPay
            }, {
              url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oYN5672jACZiK3VuXfM32L/RAAST_source.png',
              aspectRatio: 2.5 // RAAST
            }];
            // Calculate total width needed for all logos
            const totalLogosWidth = logoConfigs.reduce((sum, config) => sum + logoHeight * config.aspectRatio + logoSpacing, -logoSpacing // Remove extra spacing after last logo
            );
            // Start position for centered logos
            let currentX = (pdfWidth - totalLogosWidth) / 2;
            // Add each logo with proper aspect ratio
            logoConfigs.forEach(config => {
              const logoWidth = logoHeight * config.aspectRatio;
              pdf.addImage(config.url, 'PNG', currentX, logoY, logoWidth, logoHeight);
              currentX += logoWidth + logoSpacing;
            });
            // Add footer with generation details
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Generated on: ${new Date().toLocaleString()} | TID: ${randomTID}`, pdfWidth / 2, 280, {
              align: 'center'
            });
            // Reset text color
            pdf.setTextColor(0, 0, 0);
            // Clean up
            ReactDOM.unmountComponentAtNode(qrCodeElement);
            document.body.removeChild(tempContainer);
          } catch (error) {
            console.error('Error generating QR code:', error);
            // Fallback if html2canvas fails - create a simple placeholder
            const qrPdfSize = 140;
            const qrX = (210 - qrPdfSize) / 2;
            const qrY = 20;
            // Add a border
            pdf.setDrawColor(200, 200, 200);
            pdf.rect(qrX, qrY, qrPdfSize, qrPdfSize);
            // Add text indicating QR code
            pdf.setFontSize(12);
            pdf.setTextColor(100, 100, 100);
            pdf.text('QR Code placeholder', 105, qrY + qrPdfSize / 2, {
              align: 'center'
            });
            // Continue with merchant details
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(22);
            pdf.setFont('helvetica', 'bold');
            pdf.text(merchant.name, 105, qrY + qrPdfSize + 20, {
              align: 'center'
            });
            // Add MID and random TID
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`MID: ${merchant.merchantId}`, 105, qrY + qrPdfSize + 30, {
              align: 'center'
            });
            const randomTID = generateRandomTID();
            pdf.text(`TID: ${randomTID}`, 105, qrY + qrPdfSize + 40, {
              align: 'center'
            });
          }
        }
      }
      // Save the PDF
      const fileName = `EMVCo_QR_Codes_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      setShowDownloadModal(false);
    } else if (format === 'csv') {
      // Generate CSV metadata
      const successfulQRs = generatedQRs.filter(qr => qr.status === 'generated');
      if (successfulQRs.length === 0) {
        alert('No successful QR codes to download');
        return;
      }
      let csvContent = 'Merchant ID,Merchant Name,Till ID,Location,QR URL,Generated Date\n';
      successfulQRs.forEach(qr => {
        const merchant = merchants.find(m => m.merchantId === qr.merchantId);
        if (merchant) {
          const randomTID = generateRandomTID();
          csvContent += `${merchant.merchantId},"${merchant.name}",${randomTID},"${merchant.storeLocation}",${qr.qrUrl || 'N/A'},${new Date().toISOString()}\n`;
        }
      });
      // Create and download CSV file
      const blob = new Blob([csvContent], {
        type: 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Bulk_QR_Metadata_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setShowDownloadModal(false);
    }
  };
  return <div className="space-y-6" data-id="element-2125">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-2126">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2127">
          Bulk QR Generation
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-2128">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2129">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-2130" />
            Filters
          </button>
          <button onClick={() => setShowHistoryModal(true)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2131">
            <FileTextIcon className="h-4 w-4 mr-2" data-id="element-2132" />
            Generation History
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-2133">
        <div className="relative flex-1" data-id="element-2134">
          <input type="text" placeholder="Search merchants by name, ID, or location..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2135" />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2136">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-2137" />
          </div>
        </div>
      </div>

      {showFilters && <Card data-id="element-2138">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2139">
            <div data-id="element-2140">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2141">
                Region
              </label>
              <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2142">
                <option value="" data-id="element-2143">All Regions</option>
                <option value="North" data-id="element-2144">North</option>
                <option value="South" data-id="element-2145">South</option>
                <option value="East" data-id="element-2146">East</option>
                <option value="West" data-id="element-2147">West</option>
                <option value="Central" data-id="element-2148">Central</option>
              </select>
            </div>
            <div data-id="element-2149">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2150">
                Status
              </label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2151">
                <option value="" data-id="element-2152">All Statuses</option>
                <option value="active" data-id="element-2153">Active</option>
                <option value="inactive" data-id="element-2154">Inactive</option>
                <option value="pending" data-id="element-2155">Pending</option>
              </select>
            </div>
            <div data-id="element-2156">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2157">
                Business Type
              </label>
              <select value={businessTypeFilter} onChange={e => setBusinessTypeFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2158">
                <option value="" data-id="element-2159">All Types</option>
                <option value="Retail" data-id="element-2160">Retail</option>
                <option value="E-commerce" data-id="element-2161">E-commerce</option>
                <option value="Grocery" data-id="element-2162">Grocery</option>
                <option value="Technology" data-id="element-2163">Technology</option>
                <option value="Food & Beverage" data-id="element-2164">Food & Beverage</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-2165">
            <button onClick={() => {
          setRegionFilter('');
          setStatusFilter('');
          setBusinessTypeFilter('');
          setSearchTerm('');
        }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2166">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2167">
              Apply Filters
            </button>
          </div>
        </Card>}

      {/* QR Parameters Card */}
      <Card data-id="element-2168">
        <h2 className="text-lg font-medium mb-4" data-id="element-2169">QR Code Parameters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2170">
          <div data-id="element-2171">
            <div className="flex items-center mb-2" data-id="element-2172">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2173">
                Include Amount
              </label>
              <div className="relative inline-block w-10 ml-2 align-middle select-none" data-id="element-2174">
                <input type="checkbox" name="includeAmount" id="includeAmount" checked={qrParams.includeAmount} onChange={() => handleQRParamChange('includeAmount', !qrParams.includeAmount)} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" data-id="element-2175" />
                <label htmlFor="includeAmount" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-gray-600 cursor-pointer" data-id="element-2176"></label>
              </div>
            </div>
            {qrParams.includeAmount && <div className="mt-1" data-id="element-2177">
                <div className="relative rounded-md shadow-sm" data-id="element-2178">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2179">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-2180" />
                  </div>
                  <input type="text" placeholder="Amount" value={qrParams.amount} onChange={e => handleQRParamChange('amount', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2181" />
                </div>
              </div>}
          </div>
          <div data-id="element-2182">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2183">
              Currency
            </label>
            <select value={qrParams.currency} onChange={e => handleQRParamChange('currency', e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2184">
              <option value="PKR" data-id="element-2185">PKR - Pakistani Rupee</option>
              <option value="USD" data-id="element-2186">USD - US Dollar</option>
              <option value="EUR" data-id="element-2187">EUR - Euro</option>
              <option value="GBP" data-id="element-2188">GBP - British Pound</option>
            </select>
          </div>
          <div data-id="element-2189">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2190">
              Expiry Date (Optional)
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-2191">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2192">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2193" />
              </div>
              <input type="date" value={qrParams.expiryDate} onChange={e => handleQRParamChange('expiryDate', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2194" />
            </div>
          </div>
          <div data-id="element-2195">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2196">
              Payment Method
            </label>
            <select value={qrParams.paymentMethod} onChange={e => handleQRParamChange('paymentMethod', e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2197">
              <option value="qr" data-id="element-2198">QR Code</option>
              <option value="visa" data-id="element-2199">Visa</option>
              <option value="mastercard" data-id="element-2200">Mastercard</option>
              <option value="rtp" data-id="element-2201">RTP</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end" data-id="element-2202">
          <button onClick={handleGenerateQRs} disabled={selectedMerchants.length === 0} className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${selectedMerchants.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-2203">
            <QrCodeIcon className="h-4 w-4 mr-2" data-id="element-2204" />
            Generate QR Codes ({selectedMerchants.length} selected)
          </button>
        </div>
      </Card>

      {/* Merchant List */}
      <Card data-id="element-2205">
        <div className="mb-4 flex items-center justify-between" data-id="element-2206">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2207">
            Showing {filteredMerchants.length} merchants
          </div>
          <div className="flex items-center" data-id="element-2208">
            <input type="checkbox" id="selectAll" checked={selectAll} onChange={handleSelectAll} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2209" />
            <label htmlFor="selectAll" className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2210">
              Select All
            </label>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-2211">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2212">
            <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-2213">
              <tr data-id="element-2214">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2215">
                  <span className="sr-only" data-id="element-2216">Select</span>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2217">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2218">
                  Merchant ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2219">
                  Store Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2220">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2221">
                  Payment Methods
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2222">
                  Last QR Generated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800" data-id="element-2223">
              {filteredMerchants.length === 0 ? <tr data-id="element-2224">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-2225">
                    No merchants found matching your criteria
                  </td>
                </tr> : filteredMerchants.map(merchant => <tr key={merchant.id} className={selectedMerchants.includes(merchant.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''} data-id="element-2226">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2227">
                      <input type="checkbox" checked={selectedMerchants.includes(merchant.id)} onChange={() => handleSelectMerchant(merchant.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2228" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2229">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2230">
                        {merchant.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2231">
                        {merchant.businessType}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2232">
                      {merchant.merchantId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2233">
                      {merchant.storeLocation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2234">
                      {getStatusBadge(merchant.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2235">
                      {getPaymentMethodBadges(merchant.paymentMethods)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2236">
                      {formatDate(merchant.lastQRGenerated)}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Generation Modal */}
      <Modal isOpen={showGenerationModal} onClose={() => !isGenerating && setShowGenerationModal(false)} title="Generating QR Codes" size="lg" data-id="element-2237">
        <div className="space-y-4" data-id="element-2238">
          {/* Progress Bar */}
          <div data-id="element-2239">
            <div className="flex justify-between mb-1" data-id="element-2240">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2241">
                Progress: {generationProgress}%
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2242">
                {generatedQRs.filter(qr => qr.status !== 'pending').length} of{' '}
                {generatedQRs.length} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-2243">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{
              width: `${generationProgress}%`
            }} data-id="element-2244"></div>
            </div>
          </div>

          {/* QR Generation Status */}
          <div className="mt-4 max-h-96 overflow-y-auto" data-id="element-2245">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2246">
              <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-2247">
                <tr data-id="element-2248">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2249">
                    Merchant
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2250">
                    Merchant ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2251">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800" data-id="element-2252">
                {generatedQRs.map((qr, index) => <tr key={index} data-id="element-2253">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-2254">
                      {qr.merchantName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2255">
                      {qr.merchantId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2256">
                      {qr.status === 'pending' && <span className="flex items-center" data-id="element-2257">
                          <RefreshCwIcon className="h-4 w-4 mr-1 text-yellow-500 animate-spin" data-id="element-2258" />
                          <span className="text-yellow-500" data-id="element-2259">Pending</span>
                        </span>}
                      {qr.status === 'generated' && <span className="flex items-center" data-id="element-2260">
                          <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" data-id="element-2261" />
                          <span className="text-green-500" data-id="element-2262">Generated</span>
                        </span>}
                      {qr.status === 'failed' && <span className="flex items-center" data-id="element-2263">
                          <XIcon className="h-4 w-4 mr-1 text-red-500" data-id="element-2264" />
                          <span className="text-red-500" data-id="element-2265">Failed</span>
                          <span className="ml-2 text-xs text-gray-500" data-id="element-2266">
                            {qr.errorMessage}
                          </span>
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2267">
            {isGenerating ? <button disabled className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed" data-id="element-2268">
                <RefreshCwIcon className="h-4 w-4 mr-2 inline animate-spin" data-id="element-2269" />
                Generating...
              </button> : <>
                <button onClick={() => setShowGenerationModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2270">
                  Close
                </button>
                <button onClick={() => setShowDownloadModal(true)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2271">
                  <DownloadIcon className="h-4 w-4 mr-2 inline" data-id="element-2272" />
                  Download QR Codes
                </button>
              </>}
          </div>
        </div>
      </Modal>

      {/* Download Options Modal */}
      <Modal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} title="Download QR Codes" size="md" data-id="element-2273">
        <div className="space-y-4" data-id="element-2274">
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2275">
            Choose a download format for your QR codes:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2276">
            <button onClick={() => handleDownloadQRs('png')} className="p-4 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 flex flex-col items-center" data-id="element-2277">
              <QrCodeIcon className="h-10 w-10 text-blue-500 mb-2" data-id="element-2278" />
              <span className="text-sm font-medium" data-id="element-2279">PDF with QR Codes</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center" data-id="element-2280">
                One merchant per page with MID, TID, and DBA
              </span>
            </button>
            <button onClick={() => handleDownloadQRs('csv')} className="p-4 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 flex flex-col items-center" data-id="element-2281">
              <FileTextIcon className="h-10 w-10 text-green-500 mb-2" data-id="element-2282" />
              <span className="text-sm font-medium" data-id="element-2283">CSV Metadata</span>
              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2284">
                Download QR metadata as CSV
              </span>
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2285">
            <button onClick={() => setShowDownloadModal(false)} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2286">
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} title="Bulk QR Generation History" size="lg" data-id="element-2287">
        <div className="space-y-4" data-id="element-2288">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-2289">
            Review your previous bulk QR generation operations:
          </p>
          <div className="overflow-x-auto" data-id="element-2290">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2291">
              <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-2292">
                <tr data-id="element-2293">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2294">
                    Date & Time
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2295">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2296">
                    Merchants
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2297">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2298">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800" data-id="element-2299">
                {bulkHistory.map(item => <tr key={item.id} data-id="element-2300">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2301">
                      {new Date(item.date).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2302">
                      {item.user}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2303">
                      {item.merchantCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2304">
                      <StatusBadge status={item.status === 'completed' ? 'success' : 'warning'} text={item.status.charAt(0).toUpperCase() + item.status.slice(1)} data-id="element-2305" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-2306">
                      {item.downloadAvailable ? <button className="text-blue-600 dark:text-blue-400 hover:underline" data-id="element-2307">
                          <DownloadIcon className="h-4 w-4 inline mr-1" data-id="element-2308" />
                          Download
                        </button> : <span className="text-gray-400 dark:text-gray-500" data-id="element-2309">
                          Not available
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end" data-id="element-2310">
            <button onClick={() => setShowHistoryModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2311">
              Close
            </button>
          </div>
        </div>
      </Modal>

      <style jsx data-id="element-2312">{`
        .toggle-checkbox:checked {
          right: 0;
          border-color: #3b82f6;
        }
        .toggle-checkbox:checked + .toggle-label {
          background-color: #3b82f6;
        }
      `}</style>
    </div>;
};
export default BulkQR;